import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dumbbell,
  Home,
  User,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  ChevronLeft,
  Scale,
  Apple,
  Camera,
  LogOut,
  Timer,
  Flame,
  Activity,
  Trophy,
  Target,
  TrendingUp,
  Zap,
  Heart,
  Clock,
  Check,
  Star
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientApp = () => {
  const { brandName } = useParams();
  const navigate = useNavigate();
  
  const [brand, setBrand] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(`vexl_token_${brandName}`));
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("home");
  
  // Auth state
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "", access_code: "" });
  
  // Data state
  const [workouts, setWorkouts] = useState([]);
  const [progress, setProgress] = useState([]);
  const [nutrition, setNutrition] = useState(null);
  
  // Workout player state
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [restMode, setRestMode] = useState(false);
  
  // Progress form
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressForm, setProgressForm] = useState({ weight: "", body_fat: "", notes: "" });

  const brandColor = brand?.custom_color || "#CDF22B";

  // SEO
  useEffect(() => {
    if (brand) {
      document.title = `${brand.name} | Programa de Entrenamiento`;
    }
  }, [brand]);

  const fetchBrand = async () => {
    try {
      const res = await axios.get(`${API}/brand/${brandName}`);
      setBrand(res.data);
    } catch (error) {
      toast.error("Brand not found");
      navigate("/");
    }
    setLoading(false);
  };

  const fetchUserData = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, workoutsRes, progressRes, nutritionRes] = await Promise.all([
        axios.get(`${API}/user/profile`, { headers }),
        axios.get(`${API}/user/workouts`, { headers }),
        axios.get(`${API}/user/progress`, { headers }),
        axios.get(`${API}/user/nutrition`, { headers }).catch(() => ({ data: null }))
      ]);
      setUser(profileRes.data.user);
      setBrand(profileRes.data.brand);
      setWorkouts(workoutsRes.data);
      setProgress(progressRes.data);
      setNutrition(nutritionRes.data);
    } catch (error) {
      localStorage.removeItem(`vexl_token_${brandName}`);
      setToken(null);
      setUser(null);
    }
  }, [token, brandName]);

  useEffect(() => {
    fetchBrand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token, fetchUserData]);

  // Timer logic
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimerSeconds((s) => {
          if (restMode && s <= 1) {
            setRestMode(false);
            setTimerRunning(false);
            return 0;
          }
          return restMode ? s - 1 : s + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, restMode]);

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      if (authMode === "login") {
        const res = await axios.post(`${API}/user/login`, {
          email: authForm.email,
          password: authForm.password,
          brand_name: brandName
        });
        localStorage.setItem(`vexl_token_${brandName}`, res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        setBrand(res.data.brand);
        toast.success("Welcome back!");
      } else {
        await axios.post(`${API}/user/register`, {
          email: authForm.email,
          password: authForm.password,
          name: authForm.name,
          access_code: authForm.access_code
        });
        toast.success("Account created! Please login.");
        setAuthMode("login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(`vexl_token_${brandName}`);
    setToken(null);
    setUser(null);
    setActiveView("home");
  };

  const startWorkout = (workout) => {
    setSelectedWorkout(workout);
    setCurrentExerciseIndex(0);
    setTimerSeconds(0);
    setTimerRunning(false);
    setRestMode(false);
    setActiveView("player");
  };

  const nextExercise = () => {
    if (currentExerciseIndex < selectedWorkout.exercises.length - 1) {
      const restTime = selectedWorkout.exercises[currentExerciseIndex].rest_time || 60;
      setRestMode(true);
      setTimerSeconds(restTime);
      setTimerRunning(true);
      setCurrentExerciseIndex((i) => i + 1);
    } else {
      toast.success("Workout completed! Great job!");
      setActiveView("home");
      setSelectedWorkout(null);
    }
  };

  const prevExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex((i) => i - 1);
      setTimerSeconds(0);
      setTimerRunning(false);
      setRestMode(false);
    }
  };

  const handleProgressSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/user/progress`,
        {
          weight: progressForm.weight ? parseFloat(progressForm.weight) : null,
          body_fat: progressForm.body_fat ? parseFloat(progressForm.body_fat) : null,
          notes: progressForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgressOpen(false);
      setProgressForm({ weight: "", body_fat: "", notes: "" });
      fetchUserData();
      toast.success("Progress saved!");
    } catch (error) {
      toast.error("Error saving progress");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/user/progress/photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      fetchUserData();
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error("Error uploading photo");
    }
  };

  const initiatePayment = async () => {
    try {
      const res = await axios.post(
        `${API}/checkout/create`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error("Error initiating payment");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vexl-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin mx-auto mb-4" style={{ borderColor: `${brandColor} transparent ${brandColor} ${brandColor}` }} />
          <p className="text-white font-display">Loading...</p>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-vexl-bg flex items-center justify-center">
        <p className="text-white">Brand not found</p>
      </div>
    );
  }

  // Auth Screen - Redesigned
  if (!user) {
    return (
      <div className="min-h-screen bg-vexl-bg relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 grid-bg opacity-20" />
        
        {/* Animated Circles */}
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20 blur-3xl animate-pulse" style={{ backgroundColor: brandColor }} />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse" style={{ backgroundColor: brandColor, animationDelay: "1s" }} />
        
        {/* Header */}
        <header className="relative z-10 p-6 flex items-center justify-center">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="Logo"  className="w-12 h-12 object-cover rounded-xl" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${brandColor}20` }}>
                <Dumbbell className="w-7 h-7" style={{ color: brandColor }} />
              </div>
            )}
            <span className="font-display text-2xl font-bold text-white">{brand.name}</span>
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 flex-1 flex items-center justify-center p-6 min-h-[calc(100vh-100px)]">
          <div className="w-full max-w-md">
            {/* Welcome Message */}
            <div className="text-center mb-8">
              <h1 className="font-display text-3xl font-bold text-white mb-2">
                {authMode === "login" ? "Welcome Back!" : "Join the Team"}
              </h1>
              <p className="text-vexl-text-secondary">
                {authMode === "login" 
                  ? "Sign in to continue your fitness journey" 
                  : "Start your transformation today"}
              </p>
            </div>
            
            {/* Auth Card */}
            <div className="bg-vexl-surface/80 backdrop-blur-xl rounded-3xl p-8 border border-vexl-border shadow-2xl">
              <form onSubmit={handleAuth} className="space-y-5">
                {authMode === "register" && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm text-vexl-text-secondary">Your Name</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-vexl-text-secondary" />
                        <Input
                          data-testid="auth-name-input"
                          placeholder="John Doe"
                          value={authForm.name}
                          onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                          className="pl-12 h-14 bg-vexl-bg/50 border-vexl-border rounded-xl text-white placeholder:text-vexl-text-secondary/50 focus:border-opacity-100 transition-all"
                          style={{ borderColor: `${brandColor}30`, "--tw-ring-color": brandColor }}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-vexl-text-secondary">Access Code</label>
                      <div className="relative">
                        <Zap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-vexl-text-secondary" />
                        <Input
                          data-testid="auth-code-input"
                          placeholder="Enter your access code"
                          value={authForm.access_code}
                          onChange={(e) => setAuthForm({ ...authForm, access_code: e.target.value })}
                          className="pl-12 h-14 bg-vexl-bg/50 border-vexl-border rounded-xl text-white placeholder:text-vexl-text-secondary/50"
                          style={{ borderColor: `${brandColor}30` }}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm text-vexl-text-secondary">Email</label>
                  <div className="relative">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-vexl-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <Input
                      data-testid="auth-email-input"
                      type="email"
                      placeholder="you@email.com"
                      value={authForm.email}
                      onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                      className="pl-12 h-14 bg-vexl-bg/50 border-vexl-border rounded-xl text-white placeholder:text-vexl-text-secondary/50"
                      style={{ borderColor: `${brandColor}30` }}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-vexl-text-secondary">Password</label>
                  <div className="relative">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-vexl-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <Input
                      data-testid="auth-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                      className="pl-12 h-14 bg-vexl-bg/50 border-vexl-border rounded-xl text-white placeholder:text-vexl-text-secondary/50"
                      style={{ borderColor: `${brandColor}30` }}
                      required
                    />
                  </div>
                </div>
                
                <Button
                  data-testid="auth-submit-btn"
                  type="submit"
                  className="w-full h-14 rounded-xl font-bold text-lg shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl"
                  style={{ backgroundColor: brandColor, color: "#0A0A0A" }}
                >
                  {authMode === "login" ? "Sign In" : "Create Account"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-vexl-text-secondary">
                  {authMode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                  <button
                    data-testid="auth-toggle-btn"
                    onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
                    className="font-semibold hover:underline transition-colors"
                    style={{ color: brandColor }}
                  >
                    {authMode === "login" ? "Sign Up" : "Sign In"}
                  </button>
                </p>
              </div>
            </div>
            
            {/* Features Preview */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { icon: Dumbbell, label: "Workouts" },
                { icon: TrendingUp, label: "Progress" },
                { icon: Apple, label: "Nutrition" }
              ].map((item, i) => (
                <div key={i} className="text-center p-4 rounded-2xl bg-vexl-surface/30 backdrop-blur-sm border border-vexl-border/50">
                  <item.icon className="w-6 h-6 mx-auto mb-2" style={{ color: brandColor }} />
                  <p className="text-xs text-vexl-text-secondary">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Workout Player View - Enhanced
  if (activeView === "player" && selectedWorkout) {
    const currentExercise = selectedWorkout.exercises[currentExerciseIndex];
    const progressPercent = ((currentExerciseIndex + 1) / selectedWorkout.exercises.length) * 100;
    
    return (
      <div className="min-h-screen bg-vexl-bg flex flex-col">
        {/* Header with Progress */}
        <header className="p-4 border-b border-vexl-border">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              onClick={() => {
                setActiveView("home");
                setSelectedWorkout(null);
              }}
              className="text-vexl-text-secondary hover:text-white"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Exit
            </Button>
            <span className="font-display text-sm text-white">
              {currentExerciseIndex + 1} / {selectedWorkout.exercises.length}
            </span>
          </div>
          {/* Progress Bar */}
          <div className="h-1 bg-vexl-surface rounded-full overflow-hidden">
            <div 
              className="h-full transition-all duration-500 rounded-full"
              style={{ width: `${progressPercent}%`, backgroundColor: brandColor }}
            />
          </div>
        </header>

        {/* Exercise Display */}
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          {restMode ? (
            <div className="text-center animate-fade-in">
              <div className="w-32 h-32 rounded-full border-4 flex items-center justify-center mx-auto mb-6 animate-pulse" style={{ borderColor: brandColor }}>
                <Heart className="w-12 h-12" style={{ color: brandColor }} />
              </div>
              <p className="text-lg text-vexl-text-secondary mb-2">REST TIME</p>
              <p className="font-display text-7xl font-bold mb-4" style={{ color: brandColor }}>{timerSeconds}</p>
              <p className="text-vexl-text-secondary">
                Next: <span className="text-white">{currentExercise?.name}</span>
              </p>
            </div>
          ) : (
            <div className="w-full max-w-md animate-fade-in">
              {currentExercise?.video_url && (
                <div className="w-full aspect-video bg-vexl-surface rounded-2xl overflow-hidden mb-6 border border-vexl-border">
                  <iframe
                    src={currentExercise.video_url.replace("watch?v=", "embed/")}
                    className="w-full h-full"
                    allowFullScreen
                    title={currentExercise.name}
                  />
                </div>
              )}
              
              <h2 className="font-display text-2xl font-bold text-white text-center mb-6">
                {currentExercise?.name}
              </h2>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                  <p className="font-display text-3xl font-bold" style={{ color: brandColor }}>
                    {currentExercise?.sets}
                  </p>
                  <p className="text-xs text-vexl-text-secondary uppercase tracking-wider">Sets</p>
                </div>
                <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                  <p className="font-display text-3xl font-bold" style={{ color: brandColor }}>
                    {currentExercise?.reps}
                  </p>
                  <p className="text-xs text-vexl-text-secondary uppercase tracking-wider">Reps</p>
                </div>
                <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                  <p className="font-display text-3xl font-bold" style={{ color: brandColor }}>
                    {currentExercise?.rest_time}s
                  </p>
                  <p className="text-xs text-vexl-text-secondary uppercase tracking-wider">Rest</p>
                </div>
              </div>

              {/* Timer */}
              <div className="text-center mb-8">
                <p className="text-xs text-vexl-text-secondary uppercase tracking-wider mb-2">Elapsed Time</p>
                <p className="font-display text-5xl font-bold text-white">{formatTime(timerSeconds)}</p>
              </div>

              {/* Timer Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setTimerSeconds(0)}
                  className="w-14 h-14 rounded-full border-vexl-border"
                >
                  <RotateCcw className="w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  onClick={() => setTimerRunning(!timerRunning)}
                  className="w-20 h-20 rounded-full shadow-lg transition-all hover:scale-105"
                  style={{ backgroundColor: brandColor, color: "#0A0A0A" }}
                >
                  {timerRunning ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </Button>
                <div className="w-14 h-14" /> {/* Spacer for alignment */}
              </div>
            </div>
          )}
        </main>

        {/* Navigation */}
        <footer className="p-4 border-t border-vexl-border">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <Button
              variant="outline"
              onClick={prevExercise}
              disabled={currentExerciseIndex === 0}
              className="rounded-full border-vexl-border px-6"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </Button>
            <Button
              onClick={nextExercise}
              className="rounded-full px-6 shadow-lg"
              style={{ backgroundColor: brandColor, color: "#0A0A0A" }}
            >
              {currentExerciseIndex === selectedWorkout.exercises.length - 1 ? (
                <>
                  <Check className="w-5 h-5 mr-1" />
                  Finish
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </footer>
      </div>
    );
  }

  // Main App Views - Enhanced
  return (
    <div className="min-h-screen bg-vexl-bg pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-vexl-bg/80 border-b border-vexl-border">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="Logo" className="w-10 h-10 object-cover rounded-xl" />
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${brandColor}20` }}>
                <Dumbbell className="w-5 h-5" style={{ color: brandColor }} />
              </div>
            )}
            <span className="font-display text-lg font-bold text-white">{brand.name}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-vexl-text-secondary hover:text-white">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="p-4">
        {/* Home View */}
        {activeView === "home" && (
          <div className="space-y-6 animate-fade-in" data-testid="home-view">
            {/* Greeting Card */}
            <div className="relative overflow-hidden rounded-3xl p-6" style={{ backgroundColor: brandColor }}>
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20 -translate-y-1/2 translate-x-1/2" style={{ backgroundColor: "#0A0A0A" }} />
              <div className="relative z-10">
                <p className="text-sm opacity-80 text-vexl-bg mb-1">Welcome back</p>
                <h1 className="font-display text-2xl font-bold text-vexl-bg mb-4">
                  {user.name.split(" ")[0]}!
                </h1>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-vexl-bg/20 rounded-full px-3 py-1">
                    <Flame className="w-4 h-4 text-vexl-bg" />
                    <span className="text-sm font-medium text-vexl-bg">On fire!</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Banner */}
            {user.subscription_status !== "paid" && (
              <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-r from-[#1E45FB] to-[#1E45FB]/80 border border-[#1E45FB]/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="w-5 h-5 text-yellow-400" />
                      <p className="text-white font-semibold">Unlock Full Access</p>
                    </div>
                    <p className="text-sm text-white/70">Get all workouts for ${brand.subscription_price || "29.99"}/month</p>
                  </div>
                  <Button
                    data-testid="subscribe-btn"
                    onClick={initiatePayment}
                    className="bg-white text-[#1E45FB] hover:bg-white/90 rounded-full font-bold px-5"
                  >
                    Subscribe
                  </Button>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                <Activity className="w-6 h-6 mx-auto mb-2" style={{ color: brandColor }} />
                <p className="font-display text-xl font-bold text-white">{workouts.length}</p>
                <p className="text-xs text-vexl-text-secondary">Workouts</p>
              </div>
              <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                <Target className="w-6 h-6 mx-auto mb-2" style={{ color: brandColor }} />
                <p className="font-display text-xl font-bold text-white">{progress.length}</p>
                <p className="text-xs text-vexl-text-secondary">Check-ins</p>
              </div>
              <div className="bg-vexl-surface rounded-2xl p-4 text-center border border-vexl-border">
                <Star className="w-6 h-6 mx-auto mb-2" style={{ color: brandColor }} />
                <p className="font-display text-xl font-bold text-white">
                  {user.subscription_status === "paid" ? "Pro" : "Free"}
                </p>
                <p className="text-xs text-vexl-text-secondary">Plan</p>
              </div>
            </div>

            {/* Today's Workouts */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-semibold text-white">Today's Workouts</h2>
                <button 
                  onClick={() => setActiveView("workouts")}
                  className="text-sm flex items-center gap-1 transition-colors"
                  style={{ color: brandColor }}
                >
                  See all <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              
              {workouts.length === 0 ? (
                <div className="bg-vexl-surface rounded-2xl p-8 text-center border border-vexl-border">
                  <Dumbbell className="w-12 h-12 mx-auto mb-3 opacity-50" style={{ color: brandColor }} />
                  <p className="text-vexl-text-secondary">No workouts available yet</p>
                  <p className="text-sm text-vexl-text-secondary/70 mt-1">Check back soon!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {workouts.slice(0, 3).map((workout, index) => (
                    <div
                      key={workout.id}
                      className="group bg-vexl-surface rounded-2xl p-4 border border-vexl-border hover:border-opacity-50 transition-all cursor-pointer"
                      onClick={() => startWorkout(workout)}
                      data-testid={`workout-item-${workout.id}`}
                      style={{ "--hover-color": brandColor }}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                          style={{ backgroundColor: `${brandColor}20` }}
                        >
                          <Play className="w-6 h-6" style={{ color: brandColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{workout.name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-vexl-text-secondary">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {workout.duration_minutes} min
                            </span>
                            <span>{workout.exercises?.length || 0} exercises</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-vexl-text-secondary group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profile View */}
        {activeView === "profile" && (
          <div className="space-y-6 animate-fade-in" data-testid="profile-view">
            {/* Profile Header */}
            <div className="text-center">
              <div 
                className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold"
                style={{ backgroundColor: `${brandColor}20`, color: brandColor }}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
              <h2 className="font-display text-xl font-bold text-white">{user.name}</h2>
              <p className="text-vexl-text-secondary">{user.email}</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid="add-progress-btn"
                onClick={() => setProgressOpen(true)}
                className="h-auto py-5 rounded-2xl flex flex-col items-center gap-2 bg-vexl-surface border border-vexl-border hover:border-opacity-50"
                variant="ghost"
                style={{ "--hover-border": brandColor }}
              >
                <Scale className="w-7 h-7" style={{ color: brandColor }} />
                <span className="text-white font-medium">Log Weight</span>
              </Button>
              <label className="h-auto py-5 rounded-2xl flex flex-col items-center gap-2 cursor-pointer bg-vexl-surface border border-vexl-border hover:border-opacity-50 transition-all">
                <Camera className="w-7 h-7" style={{ color: brandColor }} />
                <span className="text-white font-medium">Add Photo</span>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </label>
            </div>

            {/* Nutrition Plan */}
            {nutrition && (
              <div className="bg-vexl-surface rounded-2xl p-5 border border-vexl-border">
                <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
                  <Apple className="w-5 h-5" style={{ color: brandColor }} />
                  My Nutrition Plan
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: "Cal", value: nutrition.calories },
                    { label: "Protein", value: `${nutrition.protein}g` },
                    { label: "Carbs", value: `${nutrition.carbs}g` },
                    { label: "Fats", value: `${nutrition.fats}g` }
                  ].map((item, i) => (
                    <div key={i} className="bg-vexl-bg rounded-xl p-3 text-center">
                      <p className="font-display text-lg font-bold" style={{ color: brandColor }}>{item.value}</p>
                      <p className="text-xs text-vexl-text-secondary">{item.label}</p>
                    </div>
                  ))}
                </div>
                {nutrition.notes && (
                  <p className="text-sm text-vexl-text-secondary mt-4 p-3 bg-vexl-bg rounded-xl">{nutrition.notes}</p>
                )}
              </div>
            )}

            {/* Progress History */}
            <div>
              <h3 className="font-display font-semibold text-white mb-4">Progress History</h3>
              {progress.length === 0 ? (
                <div className="bg-vexl-surface rounded-2xl p-8 text-center border border-vexl-border">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" style={{ color: brandColor }} />
                  <p className="text-vexl-text-secondary">No progress recorded yet</p>
                  <p className="text-sm text-vexl-text-secondary/70 mt-1">Start tracking your journey!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {progress.map((p) => (
                    <div key={p.id} className="bg-vexl-surface rounded-2xl p-4 border border-vexl-border">
                      <div className="flex items-start gap-4">
                        {p.photo_url && (
                          <img
                            src={p.photo_url}
                            alt="Progress"
                            className="w-20 h-20 object-cover rounded-xl"
                          />
                        )}
                        <div className="flex-1">
                          {p.weight && (
                            <p className="text-white flex items-center gap-2">
                              <Scale className="w-4 h-4" style={{ color: brandColor }} />
                              <span className="font-display font-bold" style={{ color: brandColor }}>{p.weight} kg</span>
                            </p>
                          )}
                          {p.body_fat && (
                            <p className="text-white flex items-center gap-2 mt-1">
                              <Activity className="w-4 h-4" style={{ color: brandColor }} />
                              <span className="font-display font-bold" style={{ color: brandColor }}>{p.body_fat}%</span>
                              <span className="text-vexl-text-secondary text-sm">body fat</span>
                            </p>
                          )}
                          {p.notes && (
                            <p className="text-sm text-vexl-text-secondary mt-2">{p.notes}</p>
                          )}
                          <p className="text-xs text-vexl-text-secondary/70 mt-2">
                            {new Date(p.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workouts View */}
        {activeView === "workouts" && (
          <div className="space-y-4 animate-fade-in" data-testid="workouts-view">
            <h2 className="font-display text-xl font-bold text-white">All Workouts</h2>
            {workouts.length === 0 ? (
              <div className="bg-vexl-surface rounded-2xl p-12 text-center border border-vexl-border">
                <Dumbbell className="w-16 h-16 mx-auto mb-4 opacity-30" style={{ color: brandColor }} />
                <p className="text-vexl-text-secondary">No workouts available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {workouts.map((workout) => (
                  <div
                    key={workout.id}
                    className="group bg-vexl-surface rounded-2xl overflow-hidden border border-vexl-border hover:border-opacity-50 transition-all cursor-pointer"
                    onClick={() => startWorkout(workout)}
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-semibold text-white">{workout.name}</h3>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${brandColor}20`, color: brandColor }}
                        >
                          {workout.category}
                        </span>
                      </div>
                      <p className="text-sm text-vexl-text-secondary mb-4 line-clamp-2">
                        {workout.description || "Ready to challenge yourself?"}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-vexl-text-secondary">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {workout.duration_minutes} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Zap className="w-4 h-4" />
                            {workout.difficulty}
                          </span>
                          <span>{workout.exercises?.length || 0} exercises</span>
                        </div>
                        <Button
                          size="sm"
                          className="rounded-full shadow-lg"
                          style={{ backgroundColor: brandColor, color: "#0A0A0A" }}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom Navigation - Enhanced */}
      <nav className="fixed bottom-0 left-0 right-0 bg-vexl-surface/95 backdrop-blur-xl border-t border-vexl-border z-50">
        <div className="flex justify-around py-2 max-w-md mx-auto">
          {[
            { id: "home", icon: Home, label: "Home" },
            { id: "workouts", icon: Dumbbell, label: "Workouts" },
            { id: "profile", icon: User, label: "Profile" }
          ].map((item) => (
            <button
              key={item.id}
              className={`flex flex-col items-center gap-1 py-3 px-6 rounded-2xl transition-all ${
                activeView === item.id ? "bg-opacity-10" : ""
              }`}
              onClick={() => setActiveView(item.id)}
              data-testid={`nav-${item.id}`}
              style={activeView === item.id ? { backgroundColor: `${brandColor}15` } : {}}
            >
              <item.icon 
                className="w-6 h-6 transition-colors" 
                style={{ color: activeView === item.id ? brandColor : "#71717A" }}
              />
              <span 
                className="text-xs font-medium transition-colors"
                style={{ color: activeView === item.id ? brandColor : "#71717A" }}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </nav>

      {/* Progress Dialog */}
      <Dialog open={progressOpen} onOpenChange={setProgressOpen}>
        <DialogContent className="bg-vexl-surface border-vexl-border rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-white text-xl">Log Your Progress</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProgressSubmit} className="space-y-5 mt-4">
            <div>
              <label className="text-sm text-vexl-text-secondary mb-2 block">Weight (kg)</label>
              <Input
                type="number"
                step="0.1"
                value={progressForm.weight}
                onChange={(e) => setProgressForm({ ...progressForm, weight: e.target.value })}
                className="h-14 bg-vexl-bg border-vexl-border rounded-xl"
                placeholder="75.5"
              />
            </div>
            <div>
              <label className="text-sm text-vexl-text-secondary mb-2 block">Body Fat %</label>
              <Input
                type="number"
                step="0.1"
                value={progressForm.body_fat}
                onChange={(e) => setProgressForm({ ...progressForm, body_fat: e.target.value })}
                className="h-14 bg-vexl-bg border-vexl-border rounded-xl"
                placeholder="15.0"
              />
            </div>
            <div>
              <label className="text-sm text-vexl-text-secondary mb-2 block">Notes</label>
              <Input
                value={progressForm.notes}
                onChange={(e) => setProgressForm({ ...progressForm, notes: e.target.value })}
                className="h-14 bg-vexl-bg border-vexl-border rounded-xl"
                placeholder="Feeling great today!"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-14 rounded-xl font-bold"
              style={{ backgroundColor: brandColor, color: "#0A0A0A" }}
            >
              Save Progress
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientApp;
